import { grpc } from '@improbable-eng/grpc-web';
import {
  StreamingRecognizeRequest,
  StreamingRecognizeResponse,
  StreamingRecognitionConfig,
  RecognitionConfig,
} from '@gen/proto/lr/speech/v2beta1/speech_pb';
import { Speech } from '@gen/proto/lr/speech/v2beta1/speech_pb_service';
import { floatTo16BitPCM, writeString } from './helpers';

export type ClientT = grpc.Client<StreamingRecognizeRequest, StreamingRecognizeResponse>;

/* Starts a streaming recognition session. */
const startStreamingRecognize = (
  sampleRate: number,
  languageCode: string,
  onMessage: (message: StreamingRecognizeResponse) => void,
  onEnd: (status: grpc.Code, message: string) => void,
  singleUtterance: boolean,
  serverUrl: string,
  timeAlign: boolean,
  serverKey: string,
) => {
  const client: ClientT = grpc.client(Speech.StreamingRecognize, {
    host: serverUrl,
    transport: grpc.WebsocketTransport(),
  });
  client.onMessage(onMessage);
  client.onEnd(onEnd);

  client.start({ authorization: `Bearer ${serverKey}` });

  const streamingConfig = new StreamingRecognitionConfig();
  streamingConfig.setInterimResults(true);
  if (singleUtterance) {
    streamingConfig.setSingleUtterance(singleUtterance);
  }
  const config = new RecognitionConfig();
  config.setEncoding(RecognitionConfig.AudioEncoding.LINEAR16);
  config.setSampleRateHertz(sampleRate);
  config.setLanguageCode(languageCode);
  if (timeAlign) config.setEnableWordTimeOffsets(timeAlign);
  config.setMaxAlternatives(1);
  streamingConfig.setConfig(config);

  const req = new StreamingRecognizeRequest();
  req.setStreamingConfig(streamingConfig);
  client.send(req);

  return client;
};

const makeWAVHeader = (numSamples: number, options: { numChannels?: number; sampleRate?: number }): ArrayBuffer => {
  if (!options.numChannels) options.numChannels = 1;
  if (!options.sampleRate) options.sampleRate = 44100;

  const buffer = new ArrayBuffer(44);
  const view = new DataView(buffer);

  /* RIFF identifier */
  writeString(view, 0, 'RIFF');
  /* RIFF chunk length */
  view.setUint32(4, 36 + numSamples * 2, true);
  /* RIFF type */
  writeString(view, 8, 'WAVE');
  /* format chunk identifier */
  writeString(view, 12, 'fmt ');
  /* format chunk length */
  view.setUint32(16, 16, true);
  /* sample format (raw) */
  view.setUint16(20, 1, true);
  /* channel count */
  view.setUint16(22, options.numChannels, true);
  /* sample rate */
  view.setUint32(24, options.sampleRate, true);
  /* byte rate (sample rate * block align) */
  view.setUint32(28, options.sampleRate * options.numChannels * 2, true);
  /* block align (channel count * bytes per sample) */
  view.setUint16(32, options.numChannels * 2, true);
  /* bits per sample */
  view.setUint16(34, 16, true);
  /* data chunk identifier */
  writeString(view, 36, 'data');
  /* data chunk length */
  view.setUint32(40, numSamples * 2, true);

  return view.buffer;
};

const encodeWAV = async (samples: Float32Array, options: { numChannels?: number; sampleRate?: number }) => {
  if (!options.numChannels) options.numChannels = 1;
  if (!options.sampleRate) options.sampleRate = 44100;

  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  /* RIFF identifier */
  writeString(view, 0, 'RIFF');
  /* RIFF chunk length */
  view.setUint32(4, 36 + samples.length * 2, true);
  /* RIFF type */
  writeString(view, 8, 'WAVE');
  /* format chunk identifier */
  writeString(view, 12, 'fmt ');
  /* format chunk length */
  view.setUint32(16, 16, true);
  /* sample format (raw) */
  view.setUint16(20, 1, true);
  /* channel count */
  view.setUint16(22, options.numChannels, true);
  /* sample rate */
  view.setUint32(24, options.sampleRate, true);
  /* byte rate (sample rate * block align) */
  view.setUint32(28, options.sampleRate * 4, true);
  /* block align (channel count * bytes per sample) */
  view.setUint16(32, options.numChannels * 2, true);
  /* bits per sample */
  view.setUint16(34, 16, true);
  /* data chunk identifier */
  writeString(view, 36, 'data');
  /* data chunk length */
  view.setUint32(40, samples.length * 2, true);

  floatTo16BitPCM(buffer, samples, 44);
  // floatTo16BitPCM(view, 44, samples);

  return view;
};

type SpeechRecognitionNodeOptions = {
  audioContext: AudioContext;
  languageCode: string;

  onMessage: (message: StreamingRecognizeResponse) => void;
  onEnd: (status: grpc.Code, message: string) => void;
  onDataAvailable?: (data: ArrayBuffer) => void;
  onInput?: (signalStrength: number) => void;

  singleUtterance?: boolean;
  serverUrl: string;
  serverKey: string;
};

class SpeechRecognitionNode {
  input: GainNode;
  processor: ScriptProcessorNode;
  audioContext: AudioContext;
  analyser: AnalyserNode;
  output: GainNode;
  _speechClient: ClientT;
  _paused: boolean;
  _closed: boolean;
  _onDataAvailable?: (data: ArrayBuffer) => void;
  _onInput?: (signalStrength: number) => void;

  constructor(options: SpeechRecognitionNodeOptions) {
    this.audioContext = options.audioContext;

    this.analyser = this.audioContext.createAnalyser();
    this.analyser.smoothingTimeConstant = 0.8;
    this.analyser.fftSize = 1024;

    this.input = this.audioContext.createGain();
    this.output = this.audioContext.createGain();

    this._onDataAvailable = options.onDataAvailable;

    this.processor = this.audioContext.createScriptProcessor(8192, 1, 1);

    this.input.connect(this.analyser);
    this.analyser.connect(this.processor);
    this.processor.onaudioprocess = this._handleProcess;

    this.processor.connect(this.output);

    this._speechClient = startStreamingRecognize(
      this.audioContext.sampleRate,
      options.languageCode,
      options.onMessage,
      options.onEnd,
      !!options.singleUtterance,
      options.serverUrl,
      true,
      options.serverKey,
    );

    this._onInput = options.onInput;

    this._speechClient.close = () => {
      console.debug('NOT CLOSING! urr');
    };

    this._paused = false;
    this._closed = false;
  }

  connect(target: AudioNode): AudioNode {
    return this.output.connect(target);
  }

  _handleProcess = (e: AudioProcessingEvent) => {
    if (this._closed) return;

    if (this._onInput) {
      // Thanks to Morphasis @ https://stackoverflow.com/questions/33322681/checking-microphone-volume-in-javascript
      const array = new Uint8Array(this.analyser.frequencyBinCount);
      this.analyser.getByteFrequencyData(array);
      const arraySum = array.reduce((a, value) => a + value, 0);
      const average = arraySum / array.length;
      this._onInput(Math.round(average));
    }

    const samples = e.inputBuffer.getChannelData(0);
    const content = new Uint8Array(samples.length * 2);
    floatTo16BitPCM(content.buffer, samples);

    if (this._onDataAvailable) {
      this._onDataAvailable(content.buffer);
    }

    const req = new StreamingRecognizeRequest();
    req.setAudioContent(content);
    this._speechClient.send(req);
  };

  pause() {
    this.audioContext.suspend();
    if (this._onInput) this._onInput(0);
  }

  resume() {
    this.audioContext.resume();
  }

  setInputVolume(volume: number) {
    if (volume < 0 || volume > 1) {
      console.error('INVALID USE: Volume must be between 0 and 1 (inclusive)');
      return;
    }
    this.input.gain.value = volume;
  }

  close() {
    if (!this._closed) {
      this._speechClient.finishSend();
      if (this._onInput) this._onInput(0);
      this._closed = true;
    }
  }
}

/* Simple processing node for StreamingSpeechRecognition */
class StreamingNode extends SpeechRecognitionNode {
  constructor(
    audioContext: AudioContext,
    sampleRate: number,
    languageCode: string,
    onMessage: (message: StreamingRecognizeResponse) => void,
    onEnd: (status: grpc.Code, message: string) => void,
    singleUtterance: boolean,
    serverUrl: string,
    serverKey: string,
  ) {
    super({ audioContext, languageCode, onMessage, onEnd, singleUtterance, serverUrl, serverKey });
  }
}

class StreamingSpeechHandlerEndEvent extends CustomEvent<{ status: grpc.Code; message: string }> {}

type StreamingSpeechHandlerOptions = {
  stream: MediaStream;
  recVolume?: number;
  languageCode: string;
  onMessage: SpeechRecognitionNodeOptions['onMessage'];
  onEnd: SpeechRecognitionNodeOptions['onEnd'];
  onAudioSrcUpdated: (audioURL: string) => void;
  onInput?: (signalStrength: number) => void;
  originalChunks?: ArrayBuffer[];
  serverUrl: string;
  serverKey: string;
};

/*
Current audio node structure:
  (Input source node)->(Input gain node)->(Analyzer node)->(Script processor node)->(Output gain node)->(Destination node)
*/

class StreamingSpeechHandler extends EventTarget {
  chunks: ArrayBuffer[];
  audioContext: AudioContext;
  inputStream: MediaStream;
  inputSrcNode: MediaStreamAudioSourceNode;
  streamProcessor: StreamingNode;
  closed: boolean;
  onAudioSrcUpdated: (url: string) => void;

  constructor(options: StreamingSpeechHandlerOptions) {
    super();
    this.chunks = options.originalChunks || [];
    this.audioContext = new AudioContext();
    this.inputStream = options.stream;
    this.closed = false;
    this.onAudioSrcUpdated = options.onAudioSrcUpdated;

    this.inputSrcNode = this.audioContext.createMediaStreamSource(this.inputStream);

    this.streamProcessor = new SpeechRecognitionNode({
      audioContext: this.audioContext,
      languageCode: options.languageCode,
      onMessage: options.onMessage,
      onEnd: (status: grpc.Code, message: string) => {
        this.dispatchEvent(new StreamingSpeechHandlerEndEvent('end', { detail: { status, message } }));
      },
      onDataAvailable: (chunk: ArrayBuffer) => {
        this.chunks.push(chunk);
      },
      onInput: options.onInput,
      serverUrl: options.serverUrl,
      serverKey: options.serverKey,
    });

    this.addEventListener('end', (event: Event) => {
      const cevent = event as StreamingSpeechHandlerEndEvent;
      options.onEnd(cevent.detail.status, cevent.detail.message);
    });

    this.streamProcessor.connect(this.audioContext.destination);
    this.inputSrcNode.connect(this.streamProcessor.input);

    if (options.recVolume !== undefined) this.setRecordingVolume(options.recVolume);
  }

  pause() {
    this.streamProcessor.pause();

    const chunks = this.chunks;
    const header = makeWAVHeader(chunks.reduce((cum, val) => cum + val.byteLength, 0) / 2, {
      sampleRate: this.audioContext.sampleRate,
    });

    const blob = new Blob([header, ...chunks], {
      type: 'audio/wav',
    });

    const audioURL = URL.createObjectURL(blob);
    this.onAudioSrcUpdated(audioURL);
  }

  resume() {
    this.streamProcessor.resume();
  }

  setRecordingVolume(volume: number) {
    this.streamProcessor.setInputVolume(volume);
  }

  close() {
    this.closed = true;
    this.inputSrcNode.disconnect();
    this.streamProcessor.input.disconnect();
    this.streamProcessor.close();
    this.audioContext.close();
    this.inputStream.getTracks().forEach((track) => track.stop());
  }
}

export {
  startStreamingRecognize,
  SpeechRecognitionNode,
  StreamingNode,
  StreamingSpeechHandler,
  StreamingSpeechHandlerEndEvent,
  encodeWAV,
  makeWAVHeader,
};
